/**
 * Add a master set - and, first, LOOK at one for free.
 *
 * This was a search box in a bottom sheet, and the owner's verdict was that it
 * was "just a little search thing in the bottom area". Worse, the only thing a
 * set row could do was commit you to chasing it, and that is not how the
 * decision actually gets made: "people don't always know what sets they want.
 * So they might be tapping in, checking the sets out, viewing them and being
 * like, no. That's not the next one I wanna build."
 *
 * So this is a screen rather than a sheet, and it has three rungs:
 *
 *   1. BROWSE, free. Eras, then the sets inside one, plus the same smart search
 *      Explore uses - type "base set" and the set is a hit. Nothing here writes.
 *   2. LOOK AT A SET, free. Its real slot count and every card in it, drawn
 *      through the one CatalogCardGrid that Explore, the flat catalogue and the
 *      card picker all draw through, with the same zoom, the same sort and
 *      filter sheet, and the same prices. Backing out costs nothing and commits
 *      nothing, which is the whole point of this phase.
 *   3. ADD, deliberate. One button, inside the set view, which opens the sub
 *      sets: the whole master, or whichever printings and ranges somebody is
 *      actually working on. That is the only thing on this screen that writes.
 *
 * The file is still AddMasterSetSheet.js because that is what the Wish tab
 * imports and what the plan names; the sheet it kept is the sub-set chooser.
 *
 * Two things here are the model rather than the layout, and both survive from
 * the version this replaces. The slot count comes from the database counting
 * (card, printing) pairs, never from card_count_total, which reads 102 for a
 * Base Set that is really 408 and claims 160 cards for a set the catalogue holds
 * none of. And a sub set is named, never numbered: "Base Set Shadowless", so
 * the row reads the way a card shop talks.
 *
 * TCG POCKET SETS ARE NOT OFFERED. A Pocket card cannot be scanned, bought or
 * traded, so a Pocket master set is a progress bar that can never move. Same
 * predicate as every other wish surface, applied to the set list rather than to
 * a tap, so nobody browses into a dead end.
 */

import { useCallback, Fragment, useEffect, useMemo, useState } from 'react';
import { useRegisterGridControls } from '../../context/GridControlsContext';
import { formatMoney } from '../../utils/catalogPrices';
import useZoom from '../../hooks/useZoom';
import { createPortal } from 'react-dom';
import {
  ChevronRight, Layers, Library, Check, Loader2,
} from 'lucide-react';
import BottomSheet, { useHideNavWhile } from '../BottomSheet';
import SmartSearchResults from '../collection/SmartSearchResults';
import { fetchSlotGrid } from '../../services/wishSlots';
import { buildSlotTiles, printingsOf } from './setSlots';
import { SlotTile } from './slotGridParts';
import { zoomGrid } from '../collection/CatalogCardGrid';
import SortFilterModal from '../modals/SortFilterModal';
import { SlotGridHeader } from './slotGridParts';
import useSmartSearch from '../../hooks/useSmartSearch';
import * as cardData from '../../services/cardData';
import { useTheme } from '../../context/ThemeContext';
import haptics from '../../utils/haptics';
import { isTcgPocketSet, TCG_POCKET_ERA_ID, useTcgPocketSets } from '../../utils/tcgPocket';
import {
  describeCatalogFilters,
  hasCatalogFilters,
  countCatalogFilters,
  EMPTY_CATALOG_FILTERS,
  DEFAULT_SEARCH_SORT,
} from '../../services/cardData/catalogFilters';
import {
  fetchSetSubsets,
  fetchSetSlots,
  createMasterSet,
  isDuplicateBinderNameError,
  duplicateBinderNameMessage,
} from '../../services/wishBinders';
import {
  readMasterBrowsePrefs, writeMasterBrowsePrefs, DEFAULT_MASTER_BROWSE_PREFS,
} from './wishPickerPrefs';

/**
 * The most cards one set read asks for. The largest set in the catalogue holds
 * a few hundred, so this is headroom rather than a limit anybody meets.
 */
const SET_PAGE_CAP = 799;

/**
 * @param {{
 *   sets?: object[],           the catalogue's sets, already in memory
 *   userId: string,
 *   theme: object,
 *   isDark?: boolean,
 *   pricingEnabled?: boolean,
 *   onBack: () => void,
 *   onAdded?: () => void,
 *   showToast?: (msg: string, kind?: string) => void,
 * }} props
 */
/**
 * Which era a set belongs to, from either set shape we get handed.
 *
 * `Collection.js` rewraps catalogue rows through `legacySet()` before passing
 * them down, and that wrapper moves the era from `era_id` to `serie.id` and the
 * logo from `logo_url` to `logo`. Reading only `era_id` meant every era counted
 * zero sets, every era row returned null, and this screen rendered completely
 * empty unless you typed a search - which is exactly what the owner hit. Read
 * both shapes rather than betting on one.
 */
const eraIdOf = (s) => s?.era_id || s?.serie?.id || null;

/**
 * How a LIST OF SETS can be ordered.
 *
 * The set list used to open the catalogue's card sort sheet, which knows about
 * rarity and HP and nothing whatever about a set row - so tapping sort appeared
 * to do nothing. These are the questions somebody actually has about sets, and
 * value is among them because "show me the expensive ones" is how people decide
 * what to chase. There is no filter pane: a list of 213 sets does not need one,
 * and the owner said so.
 */
export const SET_LIST_SORTS = [
  { id: 'release-desc', label: 'Newest first' },
  { id: 'release-asc', label: 'Oldest first' },
  { id: 'name-asc', label: 'Name A to Z' },
  { id: 'name-desc', label: 'Name Z to A' },
  { id: 'value-desc', label: 'Worth most first' },
  { id: 'value-asc', label: 'Worth least first' },
];

export const DEFAULT_SET_LIST_SORT = 'release-desc';

/** Value is the priced total of the set, from whatever prices we hold today. */
function setValue(set, totals) {
  const row = totals?.[set.id];
  const n = Number(row?.total ?? row);
  return Number.isFinite(n) ? n : 0;
}

export function sortSetRows(rows, sort, totals) {
  const out = [...(rows || [])];
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
  const byDate = (a, b) => String(a.release_date || '').localeCompare(String(b.release_date || ''));
  switch (sort) {
    case 'release-asc': return out.sort((a, b) => byDate(a, b) || byName(a, b));
    case 'name-asc': return out.sort(byName);
    case 'name-desc': return out.sort((a, b) => byName(b, a));
    case 'value-desc': return out.sort((a, b) => setValue(b, totals) - setValue(a, totals) || byName(a, b));
    case 'value-asc': return out.sort((a, b) => setValue(a, totals) - setValue(b, totals) || byName(a, b));
    case 'release-desc':
    default: return out.sort((a, b) => byDate(b, a) || byName(a, b));
  }
}

export default function AddMasterSetSheet({
  setTotals = null,
  query = '',
  onQueryChange = null,
  sets = [],
  userId,
  theme,
  isDark = true,
  pricingEnabled = false,
  onBack,
  onAdded,
  showToast,
}) {
  // So a Pocket set ingested since the last release is still left out of the
  // list rather than offered as something to chase.
  useTcgPocketSets();
  const { theme: contextTheme } = useTheme();
  const t = theme || contextTheme;

  // The page already has a search bar at the top, and this screen used to draw
  // a second one under it - two boxes, one of them inert. The owner: "notice 2
  // search bars... we should be utilizing the top one, not creating a second
  // one". So the query arrives as a prop and the setter goes back up.
  const setQuery = useCallback((next) => { onQueryChange?.(next); }, [onQueryChange]);
  const [era, setEra] = useState(null);
  const [openSet, setOpenSet] = useState(null);
  const [cards, setCards] = useState(null);
  const [slotRows, setSlotRows] = useState(null);
  const [slots, setSlots] = useState(null);
  const [sortOpen, setSortOpen] = useState(false);

  // The deliberate half: nothing below this line happens until somebody taps
  // "Chase this set".
  const [addOpen, setAddOpen] = useState(false);
  const [subsets, setSubsets] = useState(null);
  const [chosen, setChosen] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Read once on the way in rather than on every render of a grid that can hold
  // four hundred tiles. Its own storage key, so a sort chosen while shopping for
  // a set does not re-order Explore or the card picker behind it.
  // Zoom is the app's one preference, not this screen's. See
  // utils/zoomPreference.js: sort and filter are remembered per place because
  // they are about the task, zoom is not because it is about your eyes.
  const { zoom, cycleZoom } = useZoom();
  const [setListSort, setSetListSort] = useState(() => readMasterBrowsePrefs().setListSort || DEFAULT_SET_LIST_SORT);
  const [searchSort, setSearchSort] = useState(() => readMasterBrowsePrefs().searchSort);
  const [setSort, setSetSort] = useState(() => readMasterBrowsePrefs().setSort);
  const [filters, setFilters] = useState(() => readMasterBrowsePrefs().filters);

  useEffect(() => {
    writeMasterBrowsePrefs({ searchSort, setSort, filters, setListSort });
  }, [searchSort, setSort, filters, setListSort]);

  const [eras, setEras] = useState([]);
  useEffect(() => {
    let alive = true;
    cardData.getEras()
      // The Pocket era is dropped whole: none of its sets can ever be chased,
      // so an era row leading only to refusals is worse than no row.
      .then((rows) => {
        if (alive) setEras((rows || []).filter((e) => e.id !== TCG_POCKET_ERA_ID));
      })
      .catch((e) => console.warn('[master browse] era load failed', e?.message));
    return () => { alive = false; };
  }, []);

  /** Every set that can actually be chased, newest first, as getSets orders it. */
  const [listSortOpen, setListSortOpen] = useState(false);
  const setValueOf = useCallback((row) => {
    const v = setTotals?.[row.id];
    const n = Number(v?.total ?? v);
    return Number.isFinite(n) ? n : 0;
  }, [setTotals]);
  const chaseable = useMemo(
    () => sortSetRows(sets.filter((sRow) => !isTcgPocketSet(sRow.id)), setListSort, setTotals),
    [sets, setListSort, setTotals],
  );

  const smart = useSmartSearch(query, {
    eras,
    sets: chaseable,
    // Sets and eras only. This screen is shopping for a set, and a card hit
    // would be a tap with nowhere to go.
    kinds: ['era', 'set'],
    limit: 60,
    sort: searchSort,
  });

  const inSetView = Boolean(openSet);
  // The commit bar takes the nav's place while a set is open, the same way the
  // card picker's add bar does - the nav is painted by the shell and this by the
  // page, and on the phone the shell wins that fight whatever the z-index says.
  useHideNavWhile(inSetView);

  // One read per set opened, and it is the honest count: (card, printing) pairs
  // from the catalogue, which is 408 for Base Set where card_count_total says
  // 102.
  useEffect(() => {
    if (!openSet) { setSlots(null); return undefined; }
    let alive = true;
    setSlots(null);
    fetchSetSlots(openSet.id, null)
      .then((n) => { if (alive) setSlots(n); })
      .catch((e) => {
        console.warn('[master browse] slot count failed', e?.message);
        if (alive) setSlots(0);
      });
    return () => { alive = false; };
  }, [openSet]);

  // The set's cards, ordered and filtered in the database the same way every
  // other grid in the app is, so this preview is the surface people know.
  useEffect(() => {
    if (!openSet) { setCards(null); return undefined; }
    let alive = true;
    setCards(null);
    setSlotRows(null);
    // Both, because a slot is a card crossed with a printing: the slot rows say
    // which printings exist and the catalogue rows carry the art, the price and
    // whatever the sort and filters asked for.
    Promise.all([
      cardData.listCatalogCards({
        setId: openSet.id, from: 0, to: SET_PAGE_CAP, sort: setSort, filters,
      }),
      fetchSlotGrid(openSet.id, null, null).catch(() => []),
    ])
      .then(([{ rows }, slotRowsIn]) => {
        if (!alive) return;
        setCards(rows);
        setSlotRows(slotRowsIn || []);
      })
      .catch((e) => {
        console.warn('[master browse] could not read the set', e?.message);
        if (alive) { setCards([]); setSlotRows([]); }
      });
    return () => { alive = false; };
  }, [openSet, setSort, filters]);

  // Sub sets are read when the add sheet opens, not when the set does. Looking
  // at a set should cost one round trip, not two, and most looks end in a back
  // tap.
  useEffect(() => {
    if (!addOpen || !openSet) return undefined;
    let alive = true;
    setSubsets(null);
    setChosen({});
    setError('');
    fetchSetSubsets(openSet.id)
      .then((rows) => { if (alive) setSubsets(rows || []); })
      .catch((e) => {
        if (!alive) return;
        console.warn('[master set] could not read the sub sets', e?.message);
        setError('Could not read that set from the catalogue.');
        setSubsets([]);
      });
    return () => { alive = false; };
  }, [addOpen, openSet]);

  const setsInEra = useMemo(
    () => (era ? chaseable.filter((s) => eraIdOf(s) === era.id) : []),
    [era, chaseable],
  );

  const addRows = async (rows) => {
    setBusy(true);
    setError('');
    try {
      for (const row of rows) {
        // eslint-disable-next-line no-await-in-loop
        await createMasterSet(userId, row);
      }
      haptics.success();
      showToast?.(
        rows.length === 1
          ? `${rows[0].name} added to your chase list.`
          : `${rows.length} sub sets added to your chase list.`,
        'success',
      );
      setAddOpen(false);
      onAdded?.();
      // Back to browsing rather than out of the screen: somebody adding one set
      // is often adding two, and the owner's whole complaint was about being
      // thrown out of the flow.
      setOpenSet(null);
    } catch (e) {
      if (isDuplicateBinderNameError(e)) {
        setError(duplicateBinderNameMessage(e?.details?.match?.(/\((.*?)\)/)?.[1] || rows[0]?.name));
      } else {
        console.warn('[master set] add failed', e?.message);
        setError('Could not add that. Try again.');
      }
      haptics.error();
    } finally {
      setBusy(false);
    }
  };

  const addWholeMaster = () => addRows([{ setId: openSet.id, name: openSet.name, scope: null }]);

  const addChosenSubsets = () => {
    const rows = (subsets || [])
      .filter((s) => chosen[subsetKey(s)])
      // The set name leads, so a row reads "Base Set Shadowless" wherever it
      // appears - in the list, in a share, and in the celebration.
      .map((s) => ({ setId: openSet.id, name: `${openSet.name} ${s.name}`, scope: s.scope }));
    if (rows.length === 0) return undefined;
    return addRows(rows);
  };

  const chosenCount = (subsets || []).filter((s) => chosen[subsetKey(s)]).length;
  const chosenSlots = (subsets || [])
    .filter((s) => chosen[subsetKey(s)])
    .reduce((n, s) => n + (Number(s.slots) || 0), 0);

  /**
   * One action, and it does what the ticks say.
   *
   * There used to be two buttons - "add the whole master set" above the list and
   * "add N sub sets" below it - and both stayed on screen whatever was ticked, so
   * ticking a sub set changed nothing anybody could see and the counts openly
   * disagreed. Ticking nothing is the honest way to say "all of it", so the same
   * button carries both answers and its own label reports which one it is about
   * to do.
   */
  const commit = () => (chosenCount === 0 ? addWholeMaster() : addChosenSubsets());
  const commitLabel = chosenCount === 0
    // Ticking every printing IS the whole set, so this is not a dead end: it
    // says what it will do rather than refusing an empty tick list.
    ? `Chase the whole set${slots ? ` · ${slots} slots` : ''}`
    : `Chase ${chosenCount} sub ${chosenCount === 1 ? 'set' : 'sets'}${chosenSlots ? ` · ${chosenSlots} slots` : ''}`;

  /**
   * Why the sub sets do not add up to the whole set, said once, only when it is
   * true. A printing is offered as a sub set only when it covers every card, so
   * Jungle shows two 64-slot printings against a 150-slot master: the other 22
   * are printings that exist on a handful of cards - sixteen missing the
   * expansion symbol, a pre-release, a gold border, two error printings. Real
   * slots, chaseable as part of the whole set, but not a set anybody collects on
   * its own. Without this the arithmetic just looks broken.
   */
  // The printings this set actually has, and a tile per (card, printing) pair.
  const previewPrintings = useMemo(() => (slotRows ? printingsOf(slotRows) : []), [slotRows]);
  const previewTiles = useMemo(() => {
    if (!slotRows || !cards) return [];
    return buildSlotTiles({
      slots: slotRows,
      cards,
      ownership: false,
      missingOnly: false,
      // Always, for the same reason as the slot grid: the printing is the
      // difference between a $40 card and a $400 one, so it belongs on the card.
      badges: true,
    });
  }, [slotRows, cards, previewPrintings]);

  const subsetSlotSum = (subsets || []).reduce((n, s) => n + (Number(s.slots) || 0), 0);
  const oddSlots = (Number(slots) || 0) - subsetSlotSum;

  /* The page's toolbar carries these, beside the search, rather than a row of
     its own under the title. Sorting a LIST OF SETS is a real question too - by
     release date or by name - so the control is offered there as well and only
     zoom stands down, because a set row has no columns to change. */
  useRegisterGridControls({
    onZoom: inSetView ? cycleZoom : null,
    // A set row and a card are different things to order, so the control opens
    // the sheet that knows about whichever one is on screen. Pointing the set
    // list at the card sheet is why sorting appeared to do nothing.
    onSort: () => (inSetView ? setSortOpen(true) : setListSortOpen(true)),
    sortActive: inSetView ? hasCatalogFilters(filters) : setListSort !== DEFAULT_SET_LIST_SORT,
    filterCount: inSetView ? countCatalogFilters(filters) : 0,
  }, [inSetView, cycleZoom, filters, setListSort]);

  const filterNote = hasCatalogFilters(filters) ? {
    text: describeCatalogFilters(filters, eras),
    onClear: () => { haptics.tap(); setFilters(EMPTY_CATALOG_FILTERS); },
  } : null;

  /** One step back per rung, so nothing is a trapdoor out of the whole screen. */
  const back = () => {
    haptics.tap();
    if (openSet) { setOpenSet(null); return; }
    if (query) { setQuery(''); return; }
    if (era) { setEra(null); return; }
    onBack?.();
  };

  const backLabel = openSet ? 'Back to the sets' : (era || query ? 'Back to all the sets' : 'Back to your chase list');

  return (
    <div style={styles.wrap}>
      {/* The back row sits inside the content rather than up against the status
          bar, so a tap lands on the button and not on the system clock. */}
      {/* The same header the slot grids draw, so a browse rung and the set it
          opens into are recognisably the same screen: name on one line, what it
          holds on the next, controls under both. */}
      <SlotGridHeader
        title={openSet ? openSet.name : (era ? era.display_name || era.id : 'Add a master or sub set')}
        onBack={back}
        backLabel={backLabel}
        theme={t}
        progress={openSet ? { total: slots || 0, noun: 'slots', pending: slots === null } : null}
        meta={openSet ? null : 'Have a look around. Nothing is added until you say so.'}
        toolbar={null}
      />

      {inSetView ? (
        <>
          {filterNote && (
            <button
              style={{ ...styles.filterNote, backgroundColor: t.bgCard, borderColor: t.border }}
              onClick={filterNote.onClear}
            >
              <span style={{ ...styles.filterNoteText, color: t.text }}>{filterNote.text}</span>
              <span style={{ ...styles.filterNoteClear, color: t.purple }}>Clear</span>
            </button>
          )}
          {cards === null ? (
            <p style={{ ...styles.state, color: t.textSecondary }}>Loading {openSet.name}...</p>
          ) : cards.length === 0 ? (
            <p style={{ ...styles.state, color: t.textSecondary }}>
              No cards in here match all of that. Try unticking one.
            </p>
          ) : (
            /* Every SLOT, not every card.
               The header says 408 and the grid used to show 102, because it was
               drawing the catalogue's unique cards while a master set counts a
               card once per printing. The owner: "so i tap base set, i see 408
               cards. not 102. then i get it, ohhh sub set is like one of these i
               bet." Four Charizards with four different badges teach what a sub
               set IS far better than any sentence does, so the preview is the
               same slot grid the chase screen uses. Neutral: nothing is dimmed,
               because this set is not being chased yet and a faded card would
               claim otherwise. */
            <div style={{ ...zoomGrid(zoom), display: 'grid', paddingBottom: 'var(--nav-clearance)' }}>
              {previewTiles.map((tile) => (
                <SlotTile
                  key={tile.key}
                  tile={tile}
                  captions={zoom > 0}
                  theme={t}
                  isDark={isDark}
                  pocket={false}
                  pricingEnabled={pricingEnabled}
                  neutral
                  onOpen={() => {}}
                />
              ))}
            </div>
          )}
        </>
      ) : smart.active ? (
        <SmartSearchResults
          result={smart}
          theme={t}
          isDark={isDark}
          onPickEra={(e) => { haptics.tap(); setQuery(''); setEra(e); }}
          onPickSet={(s) => { haptics.tap(); setOpenSet(s); }}
          pricingEnabled={false}
          zoom={zoom}
          eras={eras}
          idleHint='Try a set name, or an era - "base set", "sword and shield".'
        />
      ) : era ? (
        <div style={styles.rows}>
          {setsInEra.map((s) => (
            <SetRow key={s.id} set={s} theme={t} onOpen={() => { haptics.tap(); setOpenSet(s); }} />
          ))}
          {setsInEra.length === 0 && (
            <p style={{ ...styles.state, color: t.textSecondary }}>
              No sets in this era yet.
            </p>
          )}
        </div>
      ) : (
        /* The sets themselves, straight away.
           This used to open on a list of eras, which made choosing a set a
           three-tap errand before you could even see one. The owner: "i tap
           master sets, it takes me to eras.... no no no... should take me to
           the sets list". An era is a heading over the sets it contains now,
           which is what it actually is - orientation while you scroll, not a
           gate to get through. Tapping an era in search still narrows to it. */
        <div style={styles.rows}>
          {chaseable.length === 0 && (
            <p style={{ ...styles.state, color: t.textSecondary }}>Loading the sets...</p>
          )}
          {chaseable.map((sRow, i) => {
            const eraId = eraIdOf(sRow);
            const prevEraId = i > 0 ? eraIdOf(chaseable[i - 1]) : null;
            const eraRow = eraId && eraId !== prevEraId
              ? eras.find((e) => e.id === eraId) || { id: eraId }
              : null;
            return (
              <Fragment key={sRow.id}>
                {eraRow && (
                  <span style={{ ...styles.sectionLabel, color: t.textMuted }}>
                    {eraRow.display_name || eraRow.id}
                  </span>
                )}
                <SetRow set={sRow} theme={t} value={setValueOf(sRow)} onOpen={() => { haptics.tap(); setOpenSet(sRow); }} />
              </Fragment>
            );
          })}
        </div>
      )}

      {/* The one thing on this screen that commits to anything, and it only
          exists once somebody has opened a set and had a look. Fixed to the
          bottom on the body, the same place and the same reason as the card
          picker's add bar: inside the page scroller iOS WebKit treats a fixed
          element like an absolute one and it never clears the nav. */}
      {/* Hidden while the sheet is open. The bar is portalled to the body and the
          sheet is not, so the bar's z-index sits in the document's stacking
          context while the sheet's larger one is trapped in a parent's - the bar
          painted straight over the sheet and buried the sub-set action under it,
          which is why picking a sub set appeared to do nothing. It is also the
          same button, so showing both was two ways to say one thing. */}
      {inSetView && !addOpen && typeof document !== 'undefined' && createPortal(
        /* Two buttons, because there are two answers.
           One button opening a sheet that then asked the same question again was
           a screen in the way of a decision already made. Chasing the whole set
           is now one tap and it is done. Picking sub sets is the only branch
           that needs a second screen, because it has to ask WHICH - and a set
           with one print run has no such branch, so it gets one button. */
        <div style={{ ...styles.addBar, backgroundColor: t.bgSheet, borderTop: `1px solid ${t.border}` }}>
          <button
            style={{ ...styles.primary, backgroundColor: t.warning, opacity: busy ? 0.6 : 1 }}
            onClick={() => { haptics.tap(); addWholeMaster(); }}
            disabled={busy}
          >
            <Layers size={16} />
            Whole set{slots ? ` · ${slots}` : ''}
          </button>
          {/* Offered only when the set HAS more than one printing, read off the
              slot rows the preview already loaded rather than paying a second
              round trip to find out. A set with one print run has no sub sets
              and gets one button. The count is left off: a printing only earns
              sub-set status when it covers every card, so the honest number is
              not known until the sheet reads it. */}
          {previewPrintings.length > 1 && (
            <button
              style={{
                ...styles.primary,
                backgroundColor: t.bgCard,
                border: `1px solid ${t.border}`,
                color: t.text,
                opacity: busy ? 0.6 : 1,
              }}
              onClick={() => { haptics.tap(); setAddOpen(true); }}
              disabled={busy}
            >
              Sub sets
            </button>
          )}
        </div>,
        document.body,
      )}

      {/* How the SET LIST is ordered. Sort only, no filter pane - a list of
          sets does not need one and the owner said so. Single choice, because
          sort is king everywhere in this app. */}
      <BottomSheet
        open={listSortOpen}
        onClose={() => setListSortOpen(false)}
        label="Sort the sets"
        overlayStyle={{ ...styles.overlay, backgroundColor: t.overlay }}
        sheetStyle={{ ...styles.sheet, backgroundColor: t.bgSheet }}
      >
        <div style={styles.handle} />
        <h2 style={{ ...styles.sheetTitle, color: t.text }}>Sort the sets</h2>
        {SET_LIST_SORTS.map((opt) => {
          const on = setListSort === opt.id;
          return (
            <button
              key={opt.id}
              style={{
                ...styles.row,
                backgroundColor: on ? t.accent : t.bgCard,
                borderColor: on ? t.accent : t.border,
              }}
              onClick={() => {
                haptics.select();
                setSetListSort(opt.id);
                setListSortOpen(false);
              }}
            >
              <span style={{ ...styles.rowName, color: on ? '#fff' : t.text }}>{opt.label}</span>
              {on && <Check size={16} color="#fff" />}
            </button>
          );
        })}
      </BottomSheet>

      {/* Mounted only while a set is open. Adding sends somebody back to the set
          list for the next one, and a sheet still rendering the set it just left
          would be reading a name that is no longer there. */}
      {openSet && (
      <BottomSheet
        open={addOpen}
        onClose={busy ? () => {} : () => setAddOpen(false)}
        label="Chase this set"
        overlayStyle={{ ...styles.overlay, backgroundColor: t.overlay }}
        sheetStyle={{ ...styles.sheet, backgroundColor: t.bgSheet }}
      >
        <div style={styles.handle} />
        <h2 style={{ ...styles.sheetTitle, color: t.text }}>{openSet?.name}</h2>
        {error && <p style={{ ...styles.error, color: t.error }}>{error}</p>}

        {subsets === null ? (
          <div style={styles.loading}>
            <Loader2 size={18} color={t.textMuted} style={styles.spin} />
            <span style={{ ...styles.hint, color: t.textMuted }}>Reading the sub sets...</span>
          </div>
        ) : (
          <>
            {subsets.length > 0 && (
              <>
                <span style={{ ...styles.sectionLabel, color: t.textMuted }}>
                  Tick the printings you want
                </span>
                {subsets.map((s) => {
                  const key = subsetKey(s);
                  const on = !!chosen[key];
                  return (
                    <button
                      key={key}
                      style={{
                        ...styles.row,
                        backgroundColor: t.bgCard,
                        borderColor: on ? t.warning : t.border,
                      }}
                      onClick={() => {
                        haptics.select();
                        setChosen((prev) => ({ ...prev, [key]: !prev[key] }));
                      }}
                    >
                      <span style={{
                        ...styles.check,
                        borderColor: on ? t.warning : t.border,
                        backgroundColor: on ? t.warning : 'transparent',
                      }}
                      >
                        {on && <Check size={12} color="#000" />}
                      </span>
                      <span style={{ ...styles.rowName, color: t.text }}>{openSet.name} {s.name}</span>
                      <span style={{ ...styles.rowMeta, color: t.textMuted }}>{s.slots} slots</span>
                    </button>
                  );
                })}
                {oddSlots > 0 && (
                  <p style={{ ...styles.hint, color: t.textMuted }}>
                    The whole set is {slots} slots because {oddSlots} of them are
                    printings that only exist on a few cards, so they are not sub
                    sets of their own.
                  </p>
                )}
              </>
            )}

            {subsets.length === 0 && (
              <p style={{ ...styles.hint, color: t.textMuted }}>
                This set has no sub sets - every card in it exists in one printing.
              </p>
            )}

            <button
              style={{ ...styles.primary, backgroundColor: t.warning }}
              onClick={commit}
              disabled={busy}
            >
              {commitLabel}
            </button>
          </>
        )}
      </BottomSheet>
      )}

      <SortFilterModal
        isOpen={sortOpen}
        onClose={() => setSortOpen(false)}
        mode="catalog"
        isOwnCollection
        sortBy={setSort}
        setSortBy={setSetSort}
        catalogFilters={filters}
        setCatalogFilters={setFilters}
        catalogEras={eras}
        onReset={() => {
          setFilters(EMPTY_CATALOG_FILTERS);
          setSetSort(DEFAULT_MASTER_BROWSE_PREFS.setSort);
          setSearchSort(DEFAULT_SEARCH_SORT);
        }}
      />
    </div>
  );
}

/** One set, on its way to being looked at rather than on its way to being added. */
function SetRow({ set, theme, onOpen, value = 0 }) {
  const logo = set.logo_url || set.logo;
  const year = String(set.release_date || '').slice(0, 4);
  return (
    <button
      style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border }}
      onClick={onOpen}
    >
      {logo ? (
        <img src={`${logo}.png`} alt="" loading="lazy" style={styles.logo} />
      ) : (
        <Library size={18} color={theme.textMuted} />
      )}
      <span style={{ ...styles.rowName, color: theme.text }}>{set.name}</span>
      <span style={styles.rowRight}>
        {value > 0 && (
          <span style={{ ...styles.rowValue, color: theme.success || '#4ade80' }}>{formatMoney(value)}</span>
        )}
        {year && <span style={{ ...styles.rowMeta, color: theme.textMuted }}>{year}</span>}
      </span>
      <ChevronRight size={16} color={theme.textSecondary} />
    </button>
  );
}

/** A sub set has no id of its own: it is a scope, so the scope is the key. */
function subsetKey(s) {
  return `${s.kind}:${s.name}:${JSON.stringify(s.scope)}`;
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 12px 24px' },
  toolbar: { display: 'flex', alignItems: 'center', gap: '8px' },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0,
    minHeight: '44px', padding: '0 12px', borderRadius: '12px', border: '1px solid',
  },
  searchInput: {
    flex: 1, minWidth: 0, backgroundColor: 'transparent', border: 'none',
    outline: 'none', fontSize: '14px',
  },
  searchClear: {
    width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'transparent', border: 'none', cursor: 'pointer', outline: 'none', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  toolBtn: {
    position: 'relative', width: '44px', height: '44px', borderRadius: '12px', border: '1px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    outline: 'none', flexShrink: 0, WebkitTapHighlightColor: 'transparent',
  },
  toolBadge: {
    position: 'absolute', top: '-4px', right: '-4px', minWidth: '16px', height: '16px',
    borderRadius: '8px', color: '#000', fontSize: '10px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
  },
  rows: { display: 'flex', flexDirection: 'column', gap: '8px', paddingBottom: 'var(--nav-clearance)' },
  row: {
    display: 'flex', alignItems: 'center', gap: '12px', minHeight: '56px', padding: '0 14px',
    borderRadius: '12px', border: '1px solid', cursor: 'pointer', textAlign: 'left',
    outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  rowName: { flex: 1, fontSize: '15px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowRight: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  rowValue: { fontSize: '12px', fontWeight: '700', fontVariantNumeric: 'tabular-nums' },
  rowMeta: { fontSize: '12px', flexShrink: 0 },
  logo: { width: '34px', height: '24px', objectFit: 'contain', flexShrink: 0 },
  filterNote: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
    width: '100%', padding: '7px 10px', borderRadius: '9px', border: '1px solid',
    cursor: 'pointer', textAlign: 'left', outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  filterNoteText: {
    fontSize: '12px', fontWeight: '600',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
  },
  filterNoteClear: { fontSize: '11px', fontWeight: '600', flexShrink: 0 },
  state: { fontSize: '13px', margin: '20px 2px', lineHeight: 1.45 },
  // The commit bar, at the bottom where a thumb already is.
  addBar: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10002,
    padding: '10px 16px calc(14px + var(--safe-area-bottom, 0px))',
  },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10004 },
  sheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: '20px 20px 0 0',
    padding: '12px 20px calc(24px + var(--safe-area-bottom, 0px))',
    zIndex: 10005,
    maxHeight: '80vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: { width: '36px', height: '4px', backgroundColor: '#3a3a3a', borderRadius: '2px', margin: '0 auto 12px' },
  sheetTitle: { fontSize: '17px', fontWeight: '600', margin: '0 0 4px' },
  hint: { fontSize: '13px', margin: '4px 0', lineHeight: 1.4 },
  sectionLabel: { fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '6px' },
  check: {
    width: '20px', height: '20px', borderRadius: '6px', border: '1px solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  primary: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    width: '100%', minHeight: '50px',
    borderRadius: '12px', border: 'none', color: '#000', fontSize: '15px', fontWeight: '700',
    cursor: 'pointer', outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  secondary: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '48px',
    borderRadius: '12px', border: '1px solid', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  error: { fontSize: '13px', margin: '0 0 2px', fontWeight: '600' },
  loading: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' },
  spin: { animation: 'spin 1s linear infinite' },
};
